import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { removeVariant, updateVariant } from '../../api/business/endpoints';
import type { ProductDetail, Variant } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatMoney, formatNumber } from '../../components/format';
import { SortableCell } from '../../components/SortableCell';
import { StatusChip } from '../../components/StatusChip';
import { TableFilters } from '../../components/TableFilters';
import { sortRows, useSort } from '../../components/useSort';
import { variantLabel } from '../../components/variants';
import { useLanguage } from '../../i18n/LanguageContext';

import { ProductAxesDialog } from './ProductAxesDialog';
import { VariantDialog } from './VariantDialog';

type VariantSortKey = 'position' | 'label' | 'sku' | 'price' | 'onHand';

type Dialog =
  { kind: 'variant'; variant: Variant | null } | { kind: 'axes' } | { kind: 'remove'; variant: Variant };

type ProductVariantsSectionProps = {
  companyId: string;
  currency: string;
  product: ProductDetail;
  /** The user may change the catalogue and the product is active. */
  canManage: boolean;
};

/**
 * What a variable product sells (ADR-0010): its options and variants, each with its values, codes, price and
 * stock. Shown only for a product that varies — a simple product's one variant never appears.
 */
export const ProductVariantsSection = ({
  companyId,
  currency,
  product,
  canManage,
}: ProductVariantsSectionProps) => {
  const { language, t } = useLanguage();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [onlyActive, setOnlyActive] = useState(false);
  const sort = useSort<VariantSortKey>('position');
  const restore = useAppMutation(
    (variantId: string) => updateVariant(companyId, product.id, variantId, { status: 'ACTIVE' }),
    ['product', 'products'],
  );
  const remove = useAppMutation(
    (variantId: string) => removeVariant(companyId, product.id, variantId),
    ['product', 'products'],
    () => setDialog(null),
  );

  const valueOf: Record<VariantSortKey, (variant: Variant) => string | number | null> = {
    position: (variant) => variant.position,
    label: (variant) => variantLabel(variant, t),
    sku: (variant) => variant.sku,
    price: (variant) => variant.effectivePriceMinor,
    onHand: (variant) => variant.onHand,
  };
  const rows = sortRows(
    product.variants.filter((variant) => !onlyActive || variant.status === 'ACTIVE'),
    sort.direction,
    valueOf[sort.key],
  );
  const missingValues =
    product.optionGroups.length > 0 && product.variants.some((variant) => variant.optionValues.length === 0);

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6">{t('variants.title')}</Typography>
          {product.optionGroups.map((group) => (
            <Chip key={group.id} size="small" label={group.name} />
          ))}
        </Stack>
        {canManage ? (
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setDialog({ kind: 'axes' })}>{t('variants.axes')}</Button>
            <Button
              startIcon={<AddIcon />}
              disabled={product.optionGroups.length === 0 || missingValues}
              onClick={() => setDialog({ kind: 'variant', variant: null })}
            >
              {t('variants.create')}
            </Button>
          </Stack>
        ) : null}
      </Stack>
      {missingValues ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
          {t('variants.needValues')}
        </Alert>
      ) : null}
      <ErrorNotice error={restore.error} />
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <TableFilters>
          <FormControlLabel
            control={
              <Switch size="small" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />
            }
            label={t('variants.onlyActive')}
          />
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="label">
                  {t('variants.values')}
                </SortableCell>
                <SortableCell sort={sort} column="sku">
                  {t('field.sku')}
                </SortableCell>
                <TableCell>{t('field.ean')}</TableCell>
                <TableCell>{t('field.mpn')}</TableCell>
                <TableCell align="right">{t('field.weightGrams')}</TableCell>
                <SortableCell sort={sort} column="price" align="right">
                  {t('batches.effectivePrice')}
                </SortableCell>
                <SortableCell sort={sort} column="onHand" align="right">
                  {t('products.onHand')}
                </SortableCell>
                <TableCell align="right">{t('products.batches')}</TableCell>
                <TableCell>{t('field.status')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((variant) => (
                <TableRow key={variant.id} hover>
                  <TableCell sx={{ fontWeight: 560 }}>
                    {variantLabel(variant, t)}
                    {variant.isDefault ? (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={t('variants.default')}
                        sx={{ ml: 1 }}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>{variant.sku ?? t('common.none')}</TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>
                    {variant.ean ?? t('common.none')}
                  </TableCell>
                  <TableCell>{variant.mpn ?? t('common.none')}</TableCell>
                  <TableCell align="right">
                    {(variant.weightGrams ?? product.weightGrams) === null
                      ? t('common.none')
                      : formatNumber(variant.weightGrams ?? product.weightGrams ?? 0, language)}
                    {variant.weightGrams === null && product.weightGrams !== null ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('variants.inherited')}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(variant.effectivePriceMinor, currency, language)}
                    {variant.priceMinor === null && product.defaultPriceMinor !== null ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('variants.inherited')}
                      </Typography>
                    ) : null}
                    {variant.unitPriceMinor !== null && product.unitLabel !== null ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('legal.unitPriceValue', {
                          price: formatMoney(variant.unitPriceMinor, currency, language),
                          unit: t(`unitSymbol.${product.unitLabel}`),
                        })}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatNumber(variant.onHand, language)}
                  </TableCell>
                  <TableCell align="right">{formatNumber(variant.batchCount, language)}</TableCell>
                  <TableCell>
                    <StatusChip status={variant.status} />
                  </TableCell>
                  <TableCell align="right">
                    {canManage ? (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button size="small" onClick={() => setDialog({ kind: 'variant', variant })}>
                          {t('common.edit')}
                        </Button>
                        {variant.status === 'ACTIVE' ? (
                          <Button
                            size="small"
                            color="warning"
                            onClick={() => setDialog({ kind: 'remove', variant })}
                          >
                            {t('variants.remove')}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            disabled={restore.isPending}
                            onClick={() => restore.mutate(variant.id)}
                          >
                            {t('common.restore')}
                          </Button>
                        )}
                      </Box>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length === 0 ? (
          <EmptyState title={t('variants.empty')} detail={t('variants.emptyDetail')} />
        ) : null}
      </Paper>

      {dialog?.kind === 'variant' ? (
        <VariantDialog
          companyId={companyId}
          currency={currency}
          product={product}
          variant={dialog.variant}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'axes' ? (
        <ProductAxesDialog companyId={companyId} product={product} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'remove' ? (
        <ConfirmDialog
          title={t('variants.removeTitle', { name: variantLabel(dialog.variant, t) })}
          message={dialog.variant.batchCount > 0 ? t('variants.removeArchive') : t('variants.removeDelete')}
          confirmLabel={t('variants.remove')}
          onConfirm={() => remove.mutate(dialog.variant.id)}
          onClose={() => setDialog(null)}
          isBusy={remove.isPending}
          error={remove.error}
        />
      ) : null}
    </>
  );
};
